import {GitHubAvatar} from '@github-ui/github-avatar'
import {useDebounce} from '@github-ui/use-debounce'
import {userHovercardPath} from '@github-ui/paths'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {PeopleIcon} from '@primer/octicons-react'
import {Box} from '@primer/react'
import type React from 'react'
import {forwardRef, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  fetchQuery,
  graphql,
  type PreloadedQuery,
  readInlineData,
  usePreloadedQuery,
  useRelayEnvironment,
  useLazyLoadQuery,
  useQueryLoader,
} from 'react-relay'

import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {ERRORS} from '../constants/errors'
import {HOTKEYS} from '../constants/hotkeys'
import {LABELS} from '../constants/labels'
import {commitUpdateIssueAssigneesBulkMutation} from '../mutations/update-issue-assignees-bulk-mutation'
import type {
  AssigneePickerAssignee$data,
  AssigneePickerAssignee$key,
} from './__generated__/AssigneePickerAssignee.graphql'
import type {
  AssigneePickerRepoUsersQuery,
  AssigneePickerRepoUsersQuery$data,
} from './__generated__/AssigneePickerRepoUsersQuery.graphql'
import type {
  AssigneePickerSearchUsersQuery,
  AssigneePickerSearchUsersQuery$data,
} from './__generated__/AssigneePickerSearchUsersQuery.graphql'
import type {AssigneePickerViewerQuery} from './__generated__/AssigneePickerViewerQuery.graphql'
import type {
  AssigneePickerFilterQuery,
  AssigneePickerFilterQuery$data,
} from './__generated__/AssigneePickerFilterQuery.graphql'
import {type ExtendedItemProps, ItemPicker, type SharedBulkActionsItemPickerProps} from './ItemPicker'
import type {ItemGroup} from '../shared'
import {SharedPicker} from './SharedPicker'
import type {updateIssueAssigneesBulkMutation$data} from '../mutations/__generated__/updateIssueAssigneesBulkMutation.graphql'
import {commitUpdateIssueAssigneesBulkByQueryMutation} from '../mutations/update-issue-assignees-bulk-by-query-mutation'
import type {updateIssueAssigneesBulkByQueryMutation$data} from '../mutations/__generated__/updateIssueAssigneesBulkByQueryMutation.graphql'
import type {AssigneePickerParticipantsQuery} from './__generated__/AssigneePickerParticipantsQuery.graphql'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {assigneesGroup, getGroupItemId, sortAssigneePickerUsers, suggestionsGroup} from './AssigneePickerUtils'
import {VALUES} from '../constants/values'
import {useKeyPress} from '@github-ui/use-key-press'
import {IS_SERVER} from '@github-ui/ssr-utils'

export type Assignee = AssigneePickerAssignee$data

export type ItemIdentifier = {
  number: number
  repo: string
  owner: string
}

type AssigneeIssuePickerProps = AssigneePickerProps & {
  itemIdentifier: ItemIdentifier
  onSelectionChange: (selectedAssignees: Assignee[]) => void
  shortcutEnabled: boolean
}

type AssigneeRepositoryPickerProps = AssigneePickerProps & {
  repoName: string
  repoOwner: string
  onSelectionChange: (assignees: Assignee[]) => void
  initialAssigneePickerViewerQueryRef?: PreloadedQuery<AssigneePickerViewerQuery, Record<string, unknown>> | null
}

type AssigneePickerBaseProps = AssigneePickerProps & AssigneePickerSearchProps

type AssigneePickerProps = {
  readonly: boolean
  assignees: Assignee[]
  viewer?: Assignee
  participants: Assignee[]
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement?: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  /**
   * Whether to render the assignee picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  shortcutHintVisible?: boolean
}

type AssigneePickerSearchProps = {
  onSelectionChange: (assignees: Assignee[]) => void
  searchAssignees: (query: string) => Promise<Assignee[]>
  isLoading?: boolean
}

type BulkAssigneePickerProps = Omit<AssigneePickerProps, 'id' | 'assignees'> & {
  repoName: string
  repoOwner: string
  assigneesAppliedToAll: AssigneePickerAssignee$data[]
  assigneesAppliedToSome: AssigneePickerAssignee$data[]
  existingIssueAssignees: Map<string, string[]>
  connectionIds: {[key: string]: string[]}
  initialAssigneePickerViewerQueryRef?: PreloadedQuery<AssigneePickerViewerQuery, Record<string, unknown>> | null
} & SharedBulkActionsItemPickerProps

const SearchAssignableIssueUsers = graphql`
  query AssigneePickerSearchUsersQuery($owner: String!, $name: String!, $number: Int!, $query: String!, $first: Int!) {
    repository(owner: $owner, name: $name) {
      issueOrPullRequest(number: $number) {
        ... on Assignable {
          suggestedAssignees(first: $first, query: $query) {
            nodes {
              ...AssigneePickerAssignee
            }
          }
        }
      }
    }
  }
`

const SearchAssignableRepositoryUsers = graphql`
  query AssigneePickerRepoUsersQuery($owner: String!, $name: String!, $query: String!, $first: Int!) {
    repository(owner: $owner, name: $name) {
      assignableUsers(query: $query, first: $first) {
        nodes {
          ...AssigneePickerAssignee
        }
      }
    }
  }
`
export const AssigneeFragment = graphql`
  fragment AssigneePickerAssignee on User @inline {
    id
    login
    name
    avatarUrl(size: 64)
  }
`

type CompressedAssigneeAnchorProps = {
  assignees: Assignee[]
  anchorProps?: React.HTMLAttributes<HTMLElement>
  displayHotkey: boolean
  MAX_DISPLAYED_ASSIGNEES?: number
  readonly?: boolean
}

const CompressedAssigneeAnchor = forwardRef<HTMLButtonElement, CompressedAssigneeAnchorProps>(
  ({assignees, anchorProps, displayHotkey, MAX_DISPLAYED_ASSIGNEES = 2, readonly}, ref) => {
    return (
      <SharedPicker
        leadingIconElement={assignees.slice(0, MAX_DISPLAYED_ASSIGNEES).map(a => (
          <GitHubAvatar
            data-hovercard-url={userHovercardPath({owner: a.login})}
            src={a.avatarUrl}
            size={16}
            alt={`@${a.login}`}
            key={a.id}
            sx={{boxShadow: '0 0 0 2px var(--bgColor-muted, var(--color-canvas-subtle))'}}
          />
        ))}
        anchorText={assignees.length > 1 ? LABELS.assignees : LABELS.noAssignees}
        anchorProps={anchorProps}
        sharedPickerMainValue={assignees
          .slice(0, MAX_DISPLAYED_ASSIGNEES)
          .map(a => a.login)
          .join(', ')
          .concat(assignees.length > MAX_DISPLAYED_ASSIGNEES ? `, ${assignees.length - MAX_DISPLAYED_ASSIGNEES}+` : '')}
        ariaLabel={LABELS.selectAssignees}
        readonly={readonly}
        leadingIcon={PeopleIcon}
        hotKey={!readonly && displayHotkey ? HOTKEYS.assigneePicker.toUpperCase() : undefined}
        ref={ref}
      />
    )
  },
)

CompressedAssigneeAnchor.displayName = 'CompressedAssigneeAnchor'

export function AssigneeRepositoryPicker({
  readonly,
  assignees,
  participants,
  repoName,
  repoOwner,
  onSelectionChange,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  initialAssigneePickerViewerQueryRef,
  nested,
  shortcutHintVisible,
  triggerOpen,
}: AssigneeRepositoryPickerProps) {
  const environment = useRelayEnvironment()

  const searchAssignees = useCallback(
    (query: string) => {
      return new Promise<Assignee[]>((resolve, reject) => {
        fetchQuery<AssigneePickerRepoUsersQuery>(environment, SearchAssignableRepositoryUsers, {
          owner: repoOwner,
          name: repoName,
          query,
          first: 10,
        }).subscribe({
          next: (data: AssigneePickerRepoUsersQuery$data) => {
            if (data !== null) {
              const suggestedAssignees = (data.repository?.assignableUsers?.nodes || []).flatMap(node =>
                // eslint-disable-next-line no-restricted-syntax
                node ? [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, node)] : [],
              )
              resolve(suggestedAssignees)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      })
    },
    [environment, repoName, repoOwner],
  )
  const props = {
    readonly,
    assignees,
    participants,
    onSelectionChange,
    searchAssignees,
    insidePortal,
    shortcutEnabled,
    anchorElement,
    nested,
    shortcutHintVisible,
    triggerOpen,
  }
  return initialAssigneePickerViewerQueryRef ? (
    <AssigneePickerBaseWithCurrentUserRef {...props} currentUserRef={initialAssigneePickerViewerQueryRef} />
  ) : (
    <AssigneePickerFetchCurrentUserOnRender {...props} />
  )
}

type LazyAssigneeIssuePickerProps = Omit<AssigneeIssuePickerProps, 'participants'>

export function LazyAssigneeIssuePicker({anchorElement, ...rest}: LazyAssigneeIssuePickerProps) {
  const [fetchKey, setFetchKey] = useState(0)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: 'Cannot edit assignees right now',
    anchorElement,
  })

  return (
    <PreloadedQueryBoundary onRetry={() => setFetchKey(fetchKey + 1)} fallback={createFallbackComponent}>
      <LazyAssigneeIssuePickerInternal {...rest} fetchKey={fetchKey} anchorElement={anchorElement} />
    </PreloadedQueryBoundary>
  )
}

function LazyAssigneeIssuePickerInternal({
  fetchKey,
  itemIdentifier,
  ...rest
}: LazyAssigneeIssuePickerProps & {fetchKey: number}) {
  const {owner, repo, number} = itemIdentifier
  const {repository} = useLazyLoadQuery<AssigneePickerParticipantsQuery>(
    graphql`
      query AssigneePickerParticipantsQuery($owner: String!, $repo: String!, $number: Int!) {
        repository(owner: $owner, name: $repo) {
          issue(number: $number) {
            participants(first: 10) {
              edges {
                node {
                  ...AssigneePickerAssignee
                }
              }
            }
          }
        }
      }
    `,
    {
      repo,
      owner,
      number,
    },
    {fetchKey},
  )

  const participants = useMemo(() => {
    return (repository?.issue?.participants?.edges || []).flatMap(assignee =>
      // eslint-disable-next-line no-restricted-syntax
      assignee && assignee.node ? [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, assignee.node)] : [],
    )
  }, [repository])

  return <AssigneeIssuePicker {...rest} participants={participants} itemIdentifier={itemIdentifier} />
}

export function AssigneeIssuePicker({itemIdentifier, viewer, ...rest}: AssigneeIssuePickerProps) {
  const environment = useRelayEnvironment()

  const {owner, repo, number} = itemIdentifier

  const searchAssignees = useCallback(
    (query: string) => {
      return new Promise<Assignee[]>((resolve, reject) => {
        fetchQuery<AssigneePickerSearchUsersQuery>(environment, SearchAssignableIssueUsers, {
          owner,
          name: repo,
          first: 10,
          number,
          query,
        }).subscribe({
          next: (data: AssigneePickerSearchUsersQuery$data) => {
            if (data !== null) {
              const suggestedAssignees = (data.repository?.issueOrPullRequest?.suggestedAssignees?.nodes || []).flatMap(
                // eslint-disable-next-line no-restricted-syntax
                node => (node ? [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, node)] : []),
              )
              resolve(suggestedAssignees)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      })
    },
    [environment, number, owner, repo],
  )

  if (viewer) {
    return <AssigneePickerBaseInternal viewer={viewer} searchAssignees={searchAssignees} {...rest} />
  } else {
    return <AssigneePickerFetchCurrentUserOnRender viewer={viewer} searchAssignees={searchAssignees} {...rest} />
  }
}

export const AssigneePickerViewerGraphqlQuery = graphql`
  query AssigneePickerViewerQuery {
    viewer {
      ...AssigneePickerAssignee
    }
  }
`

export const AssigneePickerFilterGraphqlQuery = graphql`
  query AssigneePickerFilterQuery($owner: String!, $name: String!, $query: String!, $first: Int!) {
    repository(owner: $owner, name: $name) {
      assignableUsers(loginNames: $query, first: $first) {
        edges {
          node {
            ...AssigneePickerAssignee
          }
        }
      }
    }
    viewer {
      ...AssigneePickerAssignee
    }
  }
`
export function LazyAssigneeRepoPickerOnClick({
  shortcutEnabled,
  anchorElement,
  ...props
}: AssigneeRepositoryPickerProps & {assigneeTokens: string[]}) {
  const [wasTriggered, setWasTriggered] = useState(false)

  useKeyPress(shortcutEnabled ? [HOTKEYS.assigneePicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered && anchorElement) {
    return anchorElement({onClick: () => setWasTriggered(true)})
  }

  return (
    <AssigneePickerFetchUsersOnRender
      shortcutEnabled={shortcutEnabled}
      anchorElement={anchorElement}
      triggerOpen={true}
      {...props}
    />
  )
}

function AssigneePickerFetchUsersOnRender({
  repoName,
  repoOwner,
  assigneeTokens,
  ...props
}: AssigneeRepositoryPickerProps & {assigneeTokens: string[]}) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState<AssigneePickerFilterQuery$data | null>(null)

  useEffect(() => {
    if (!IS_SERVER) {
      clientSideRelayFetchQueryRetained<AssigneePickerFilterQuery>({
        environment,
        query: AssigneePickerFilterGraphqlQuery,
        variables: {owner: repoOwner, name: repoName, query: assigneeTokens.join(','), first: assigneeTokens.length},
      }).subscribe({
        next: internalData => {
          setData(internalData ?? null)
          setIsLoading(false)
        },
        error: () => {
          setIsLoading(false)
        },
      })
    }
  }, [environment, repoOwner, repoName, assigneeTokens])

  const searchAssignees = useCallback(
    (query: string) => {
      return new Promise<Assignee[]>((resolve, reject) => {
        fetchQuery<AssigneePickerRepoUsersQuery>(environment, SearchAssignableRepositoryUsers, {
          owner: repoOwner,
          name: repoName,
          query,
          first: 10,
        }).subscribe({
          next: (response: AssigneePickerRepoUsersQuery$data) => {
            if (response !== null) {
              const suggestedAssignees = (response.repository?.assignableUsers?.nodes || []).flatMap(node =>
                // eslint-disable-next-line no-restricted-syntax
                node ? [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, node)] : [],
              )
              resolve(suggestedAssignees)
            }
          },
          error: (error: Error) => {
            reject(error)
          },
        })
      })
    },
    [environment, repoName, repoOwner],
  )

  // eslint-disable-next-line no-restricted-syntax
  const viewer = readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, data?.viewer || null)

  const participants = (data?.repository?.assignableUsers?.edges || []).flatMap(participant =>
    participant && participant.node
      ? // eslint-disable-next-line no-restricted-syntax
        [readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, participant.node)]
      : [],
  )

  return (
    <AssigneePickerBaseInternal
      {...props}
      isLoading={isLoading}
      viewer={viewer || undefined}
      assignees={participants}
      searchAssignees={searchAssignees}
    />
  )
}

function AssigneePickerFetchCurrentUserOnRender(props: AssigneePickerBaseProps) {
  const [currentUserRef, loadCurrentUser, disposeCurrentUser] = useQueryLoader<AssigneePickerViewerQuery>(
    AssigneePickerViewerGraphqlQuery,
  )

  useEffect(() => {
    loadCurrentUser({})
    return () => {
      disposeCurrentUser()
    }
  }, [loadCurrentUser, disposeCurrentUser])

  if (!currentUserRef) return null

  return <AssigneePickerBaseWithCurrentUserRef {...props} currentUserRef={currentUserRef} />
}

function AssigneePickerBaseWithCurrentUserRef({
  currentUserRef,
  ...props
}: AssigneePickerBaseProps & {currentUserRef: PreloadedQuery<AssigneePickerViewerQuery>}) {
  const data = usePreloadedQuery<AssigneePickerViewerQuery>(AssigneePickerViewerGraphqlQuery, currentUserRef)
  // eslint-disable-next-line no-restricted-syntax
  const viewer = readInlineData<AssigneePickerAssignee$key>(AssigneeFragment, data.viewer)

  return <AssigneePickerBaseInternal {...props} viewer={viewer} />
}

function AssigneePickerBaseInternal({
  readonly,
  assignees,
  participants,
  searchAssignees,
  onSelectionChange,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  viewer,
  nested,
  isLoading = false,
  triggerOpen,
  shortcutHintVisible = true,
}: AssigneePickerBaseProps & {viewer?: Assignee}) {
  const [filter, setFilter] = useState('')
  const [searchResults, setSearchResults] = useState<Assignee[] | undefined>(undefined)
  const {addToast} = useToastContext()

  const getItemKey = useCallback((assignee: Assignee) => assignee.login, [])

  const items = useMemo(() => {
    let sortedItems = []
    if (searchResults) {
      sortedItems = sortAssigneePickerUsers(assignees, searchResults, filter)
    } else {
      sortedItems = sortAssigneePickerUsers(
        assignees,
        assignees.concat(
          participants.filter(participant => !assignees.find(assignee => assignee.login === participant.login)),
        ),
        filter,
      )
    }

    if (viewer) {
      const currentUserIndex = sortedItems.findIndex(item => item.login === viewer.login)
      if (currentUserIndex !== -1) {
        // Remove the current user from its current position in the list to avoid duplicates
        sortedItems.splice(currentUserIndex, 1)
      } else if (searchResults) {
        // If the current user is not in the search results, we don't want to show it in the suggestions
        return sortedItems
      }
      // Add the current user to the top of the list
      sortedItems.unshift(viewer)
    }

    return sortedItems
  }, [searchResults, assignees, filter, participants, viewer])

  const groupItemId = useCallback((assignee: Assignee) => getGroupItemId(assignees, assignee), [assignees])

  const convertToItemProps = useCallback(
    (assignee: Assignee): ExtendedItemProps<Assignee> => {
      return {
        id: assignee.id,
        text: assignee.login,
        description: assignee.name ?? '',
        source: assignee,
        groupId: groupItemId(assignee),
        leadingVisual: () => <GitHubAvatar alt={assignee.login} src={assignee.avatarUrl} />,
      }
    },
    [groupItemId],
  )

  const onClose = useCallback(() => {
    setSearchResults(undefined)
  }, [])

  const fetchSearchData = useCallback(
    async (query: string) => {
      if (query === '') {
        setSearchResults(undefined)
        return
      }

      try {
        const suggestedAssignees = await searchAssignees(query)
        setSearchResults(suggestedAssignees)
      } catch {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchAssignees,
        })
      }
    },
    [addToast, searchAssignees],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => fetchSearchData(nextValue),
    VALUES.pickerDebounceTime,
    {
      leading: true,
      trailing: true,
      onChangeBehavior: 'flush',
    },
  )

  const filterItems = useCallback(
    (value: string) => {
      const trimmedFilter = value.trim()
      if (filter !== trimmedFilter) {
        debounceFetchSearchData(trimmedFilter)
      }
      setFilter(value)
    },
    [debounceFetchSearchData, filter],
  )

  const assigneePickerRef = useRef<HTMLButtonElement>(null)

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      if (anchorElement) {
        return anchorElement(anchorProps)
      }

      return (
        <CompressedAssigneeAnchor
          assignees={assignees}
          displayHotkey={shortcutEnabled && !readonly && shortcutHintVisible}
          anchorProps={readonly ? undefined : anchorProps}
          readonly={readonly}
          ref={assigneePickerRef}
        />
      )
    },
    [anchorElement, assignees, shortcutHintVisible, readonly, shortcutEnabled],
  )

  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []

    // Find items that are assignees and suggestions (non-assignees are suggestion items)
    const assigneeItems = items.filter(i => assignees.find(a => a.id === i.id))
    const suggestionItems = items.filter(i => !assignees.find(a => a.id === i.id))

    if (assigneeItems.length > 0) {
      itemGroups.push(assigneesGroup)
    }
    if (suggestionItems.length > 0) {
      itemGroups.push(suggestionsGroup)
    }
    return itemGroups
  }, [assignees, items])

  return (
    <Box sx={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1}}>
      <ItemPicker
        items={items}
        initialSelectedItems={assignees}
        loading={isLoading}
        openHotkey={shortcutEnabled ? HOTKEYS.assigneePicker : undefined}
        filterItems={filterItems}
        title={LABELS.assigneesHeader}
        getItemKey={getItemKey}
        triggerOpen={triggerOpen}
        convertToItemProps={convertToItemProps}
        placeholderText={LABELS.filterUsers}
        selectionVariant="multiple"
        onSelectionChange={onSelectionChange}
        onClose={onClose}
        renderAnchor={renderAnchor}
        groups={groups}
        insidePortal={insidePortal}
        height={'large'}
        width={'medium'}
        nested={nested}
        resultListAriaLabel={'User results'}
        selectPanelRef={assigneePickerRef}
      />
    </Box>
  )
}

export function BulkIssuesAssigneePicker({
  useQueryForAction,
  repositoryId,
  query,
  issuesToActOn,
  assigneesAppliedToAll,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Will be in use once SelectPanel support partial selection
  assigneesAppliedToSome,
  existingIssueAssignees,
  connectionIds,
  onError,
  onCompleted,
  ...rest
}: BulkAssigneePickerProps) {
  const environment = useRelayEnvironment()

  const onSelectionChange = useCallback(
    (selectedAssignees: AssigneePickerAssignee$data[]) => {
      const assigneesToAdd = selectedAssignees.filter(a => !assigneesAppliedToAll.some(aa => aa.id === a.id))
      const assigneesToRemove = assigneesAppliedToAll.filter(a => !selectedAssignees.some(aa => aa.id === a.id))
      const args = {
        applyAssigneeIds: assigneesToAdd.map(l => l.id),
        removeAssigneeIds: assigneesToRemove.map(l => l.id),
      }
      const mutationArgs = {
        environment,
        optimisticUpdateIds: issuesToActOn,
        existingIssueAssignees,
        connectionIds,
        onError: (error: Error) => {
          onError?.(error)
        },
      }
      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueAssigneesBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            ...args,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueAssigneesBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueAssigneesBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            ...args,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueAssigneesBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [
      assigneesAppliedToAll,
      connectionIds,
      environment,
      existingIssueAssignees,
      issuesToActOn,
      onCompleted,
      onError,
      query,
      repositoryId,
      useQueryForAction,
    ],
  )

  return <AssigneeRepositoryPicker {...rest} assignees={assigneesAppliedToAll} onSelectionChange={onSelectionChange} />
}

try{ AssigneeRepositoryPicker.displayName ||= 'AssigneeRepositoryPicker' } catch {}
try{ LazyAssigneeIssuePicker.displayName ||= 'LazyAssigneeIssuePicker' } catch {}
try{ LazyAssigneeIssuePickerInternal.displayName ||= 'LazyAssigneeIssuePickerInternal' } catch {}
try{ AssigneeIssuePicker.displayName ||= 'AssigneeIssuePicker' } catch {}
try{ LazyAssigneeRepoPickerOnClick.displayName ||= 'LazyAssigneeRepoPickerOnClick' } catch {}
try{ AssigneePickerFetchUsersOnRender.displayName ||= 'AssigneePickerFetchUsersOnRender' } catch {}
try{ AssigneePickerFetchCurrentUserOnRender.displayName ||= 'AssigneePickerFetchCurrentUserOnRender' } catch {}
try{ AssigneePickerBaseWithCurrentUserRef.displayName ||= 'AssigneePickerBaseWithCurrentUserRef' } catch {}
try{ AssigneePickerBaseInternal.displayName ||= 'AssigneePickerBaseInternal' } catch {}
try{ BulkIssuesAssigneePicker.displayName ||= 'BulkIssuesAssigneePicker' } catch {}